import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Shield, FileText, Loader2, RefreshCw, ChevronRight, Download,
  AlertTriangle, CheckCircle2, XCircle, Clock, Zap, Building2, Target
} from 'lucide-react';

const SCORE_COLOR = (s) => s >= 80 ? 'text-emerald-600' : s >= 60 ? 'text-amber-600' : 'text-red-600';
const SCORE_BG = (s) => s >= 80 ? 'bg-emerald-50 border-emerald-200' : s >= 60 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
const STATUS_ICON = { COMPLIANT: CheckCircle2, NEEDS_ATTENTION: AlertTriangle, NON_COMPLIANT: XCircle };
const STATUS_COLOR = { COMPLIANT: 'text-emerald-600', NEEDS_ATTENTION: 'text-amber-600', NON_COMPLIANT: 'text-red-600' };
const RISK_COLOR = { LOW: 'bg-emerald-600', MODERATE: 'bg-amber-500', HIGH: 'bg-orange-600', CRITICAL: 'bg-red-600' };

export default function ComplianceHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Compliance');
  const [view, setView] = useState('generate');
  const [reportTypes, setReportTypes] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [selectedType, setSelectedType] = useState('full_enterprise');
  const [periodDays, setPeriodDays] = useState(30);
  const [orgName, setOrgName] = useState('EIGOO Inc');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [typesRes, reportsRes] = await Promise.all([
        api.get('/api/compliance/report-types'),
        api.get('/api/compliance/reports'),
      ]);
      setReportTypes(typesRes.data.report_types || []);
      setReports(reportsRes.data.reports || []);
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/api/compliance/generate', { report_type: selectedType, period_days: periodDays, org_name: orgName });
      setSelectedReport(data);
      setView('detail');
      toast.success('Compliance report generated!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const viewReport = async (id) => {
    try {
      const { data } = await api.get(`/api/compliance/reports/${id}`);
      setSelectedReport(data);
      setView('detail');
    } catch { toast.error('Failed to load report'); }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  const ai = selectedReport?.ai_analysis;

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="compliance-hub">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
                <Shield className="w-6 h-6 text-[#D4A843]" /> Compliance Center
              </h1>
              <p className="text-sm text-gray-500 mt-1">AI-powered regulatory compliance reports across all domains</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          </div>

          <div className="flex gap-1 border-b border-gray-200 pb-0">
            {[{k:'generate',l:'Generate Report'},{k:'history',l:'Report History'}].map(t => (
              <button key={t.k} onClick={() => { setView(t.k); setSelectedReport(null); }} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${view === t.k || (view === 'detail' && t.k === 'history') ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`compliance-tab-${t.k}`}>{t.l}</button>
            ))}
          </div>

          {/* Generate Report */}
          {view === 'generate' && (
            <div className="space-y-4" data-testid="compliance-generate">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reportTypes.map(rt => (
                  <div key={rt.id} onClick={() => setSelectedType(rt.id)} className={`cursor-pointer rounded-lg p-5 border-2 transition-all hover-lift ${selectedType === rt.id ? 'border-[#D4A843] bg-[#D4A843]/5' : 'border-gray-200 bg-white'}`} data-testid={`report-type-${rt.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <Shield className={`w-5 h-5 ${selectedType === rt.id ? 'text-[#D4A843]' : 'text-gray-400'}`} />
                      {selectedType === rt.id && <CheckCircle2 className="w-4 h-4 text-[#D4A843]" />}
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">{rt.name}</h3>
                    <p className="text-[10px] text-gray-500 mt-1">{rt.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {rt.frameworks?.map((f, i) => <span key={`item-${i}`} className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">{f}</span>)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Report Settings</h3>
                <div className="flex items-end gap-4">
                  <div>
                    <label className="text-xs text-gray-600">Organization</label>
                    <input value={orgName} onChange={e => setOrgName(e.target.value)} className="mt-1 h-9 rounded-lg border border-gray-200 px-3 text-sm w-48" data-testid="compliance-org" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Period</label>
                    <select value={periodDays} onChange={e => setPeriodDays(Number(e.target.value))} className="mt-1 h-9 rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="compliance-period">
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                      <option value={180}>Last 6 months</option>
                      <option value={365}>Last year</option>
                    </select>
                  </div>
                  <Button onClick={generateReport} disabled={generating} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="compliance-generate-btn">
                    {generating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
                    {generating ? 'Generating...' : 'Generate Report'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Report History */}
          {view === 'history' && (
            <div className="space-y-3" data-testid="compliance-history">
              {reports.length === 0 && <p className="text-center py-12 text-gray-400 bg-white rounded-lg border border-gray-200">No reports generated yet.</p>}
              {reports.map(r => (
                <div key={r.id} onClick={() => viewReport(r.id)} className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover-lift" data-testid={`report-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-[#D4A843]" />
                      <div>
                        <p className="text-sm font-bold text-gray-900">{r.type_name}</p>
                        <p className="text-[10px] text-gray-400">{r.org_name} | {r.period_days} days | {new Date(r.generated_at).toLocaleDateString()} | By {r.generated_by}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {r.ai_generated && <span className="text-[10px] bg-violet-100 text-violet-700 px-2 py-0.5 rounded font-bold">AI</span>}
                      <div className="flex flex-wrap gap-1">{r.frameworks?.map((f, i) => <span key={`item-${i}`} className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">{f}</span>)}</div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Report Detail */}
          {view === 'detail' && selectedReport && (
            <div className="space-y-4 animate-fade-in" data-testid="compliance-detail">
              <button onClick={() => setView('history')} className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1 mb-2">Back to reports</button>

              {/* Header */}
              <div className={`rounded-xl p-6 ${ai ? RISK_COLOR[ai.risk_level] || 'bg-[#D4A843]' : 'bg-[#D4A843]'} text-[#0A0A0A]`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider opacity-80">{selectedReport.type_name}</p>
                    <p className="text-xl font-bold mt-1">{selectedReport.org_name}</p>
                    <p className="text-xs opacity-80 mt-0.5">Period: {selectedReport.period_days} days | Generated: {new Date(selectedReport.generated_at).toLocaleString()}</p>
                  </div>
                  {ai && (
                    <div className="text-right">
                      <p className="text-4xl font-bold">{ai.overall_score}</p>
                      <p className="text-[10px] uppercase tracking-wider opacity-80">{ai.risk_level} Risk</p>
                    </div>
                  )}
                </div>
                {ai?.executive_summary && <p className="mt-4 text-sm opacity-90 leading-relaxed">{ai.executive_summary}</p>}
              </div>

              {/* Compliance Areas */}
              {ai?.compliance_areas?.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Compliance Areas</h3>
                  {ai.compliance_areas.map((area, i) => {
                    const Icon = STATUS_ICON[area.status] || Shield;
                    return (
                      <div key={`item-${i}`} className={`border rounded-lg p-5 ${SCORE_BG(area.score)}`} data-testid={`area-${i}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${STATUS_COLOR[area.status]}`} />
                            <h4 className="text-sm font-bold text-gray-900">{area.area}</h4>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xl font-bold ${SCORE_COLOR(area.score)}`}>{area.score}</span>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${area.status === 'COMPLIANT' ? 'bg-emerald-100 text-emerald-700' : area.status === 'NEEDS_ATTENTION' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{area.status?.replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                        {area.findings?.length > 0 && (
                          <div className="mb-2"><p className="text-[10px] font-bold text-gray-600 mb-1">Findings:</p>{area.findings.map((f, j) => <p key={`j-${j}`} className="text-xs text-gray-600 flex items-start gap-1"><span className="text-gray-400 mt-0.5">-</span>{f}</p>)}</div>
                        )}
                        {area.recommendations?.length > 0 && (
                          <div><p className="text-[10px] font-bold text-[#D4A843] mb-1">Recommendations:</p>{area.recommendations.map((r, j) => <p key={`j-${j}`} className="text-xs text-gray-600 flex items-start gap-1"><Zap className="w-3 h-3 text-[#D4A843] flex-shrink-0 mt-0.5" />{r}</p>)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Critical Findings */}
              {ai?.critical_findings?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                  <h3 className="text-xs font-bold text-red-800 uppercase tracking-wider mb-2">Critical Findings</h3>
                  {ai.critical_findings.map((f, i) => <p key={`item-${i}`} className="text-sm text-red-700 flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />{f}</p>)}
                </div>
              )}

              {/* Action Items */}
              {ai?.action_items?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Action Items</h3>
                  <div className="space-y-2">
                    {ai.action_items.map((a, i) => (
                      <div key={`item-${i}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${a.priority === 'P0' ? 'bg-red-600 text-white' : a.priority === 'P1' ? 'bg-orange-500 text-white' : 'bg-gray-400 text-white'}`}>{a.priority}</span>
                          <p className="text-sm text-gray-900">{a.action}</p>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-gray-500">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{a.timeline}</span>
                          <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{a.owner}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Data Summary */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Data Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {selectedReport.data?.operations && (
                    <>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-gray-900">{selectedReport.data.operations.total_tickets}</p><p className="text-[10px] text-gray-500">Total Tickets</p></div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-emerald-600">{selectedReport.data.operations.compliance_pct}%</p><p className="text-[10px] text-gray-500">Resolution Rate</p></div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-[#D4A843]">{selectedReport.data.operations.sla_compliance_pct}%</p><p className="text-[10px] text-gray-500">SLA Compliance</p></div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-red-600">{selectedReport.data.operations.critical_tickets}</p><p className="text-[10px] text-gray-500">Critical Tickets</p></div>
                    </>
                  )}
                  {selectedReport.data?.security && (
                    <>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-gray-900">{selectedReport.data.security.total_events?.toLocaleString()}</p><p className="text-[10px] text-gray-500">Security Events</p></div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-red-600">{selectedReport.data.security.flagged_events}</p><p className="text-[10px] text-gray-500">IOCs Detected</p></div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-[#D4A843]">{selectedReport.data.security.agents_active}</p><p className="text-[10px] text-gray-500">Active Agents</p></div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-gray-900">{selectedReport.data.security.unique_hosts}</p><p className="text-[10px] text-gray-500">Affected Hosts</p></div>
                    </>
                  )}
                  {selectedReport.data?.cannabis && (
                    <>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-gray-900">{selectedReport.data.cannabis.plants_total}</p><p className="text-[10px] text-gray-500">Plants Tracked</p></div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-gray-900">{selectedReport.data.cannabis.packages_total}</p><p className="text-[10px] text-gray-500">Packages</p></div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-gray-900">{selectedReport.data.cannabis.transfers_total}</p><p className="text-[10px] text-gray-500">Transfers</p></div>
                      <div className="text-center p-3 bg-gray-50 rounded-lg"><p className="text-lg font-bold text-emerald-600">{selectedReport.data.cannabis.lab_passed}</p><p className="text-[10px] text-gray-500">Lab Passed</p></div>
                    </>
                  )}
                </div>
              </div>

              {ai?.regulatory_notes?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-blue-800 mb-2">Regulatory Notes</h3>
                  {ai.regulatory_notes.map((n, i) => <p key={`item-${i}`} className="text-xs text-blue-700">{n}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
